import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Globe, CheckCircle2, Loader2, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import { Overline, Reveal } from '../components/Reveal';
import { useSEO } from '../lib/seo';
import { CONTACT, SERVICES } from '../data/content';

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FORM_ENDPOINT = process.env.REACT_APP_CONTACT_FORM_ENDPOINT;

const NEXT_STEPS = [
    { title: 'We respond within two business days', text: 'A practitioner reviews every enquiry personally — no automated routing, no sales handoffs.' },
    { title: 'A focused 30-minute consultation', text: 'We discuss your current posture, your most plausible disruption scenarios, and where the gaps are likely to be.' },
    { title: 'A clear, scoped proposal', text: 'You receive a right-sized recommendation with defined deliverables, timelines, and measurable outcomes.' },
];

const inputClass = 'w-full rounded-xl border border-slate-700 bg-navy-800/60 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors duration-200 focus:border-deepteal/50 focus:bg-navy-800';

const ContactForm = () => {
    const [form, setForm] = useState({ name: '', email: '', company: '', service: '', message: '' });
    const [status, setStatus] = useState('idle');

    const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus('sending');
        try {
            const res = FORM_ENDPOINT
                ? await fetch(FORM_ENDPOINT, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({ ...form, subject: `Planmodo website enquiry — ${form.name}` }),
                })
                : await fetch(`${API}/contact`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(form),
                });
            if (!res.ok) throw new Error('Request failed');
            setStatus('success');
            toast.success('Enquiry sent. A practitioner will respond within two business days.');
        } catch (err) {
            setStatus('idle');
            toast.error('Something went wrong. Please try again or email us directly.');
        }
    };

    if (status === 'success') {
        return (
            <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="flex h-full flex-col items-center justify-center rounded-2xl border border-deepteal/20 bg-navy-800/40 p-10 text-center"
                data-testid="contact-success-panel"
            >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-deepteal/10 border border-deepteal/30">
                    <CheckCircle2 size={28} className="text-deepteal" />
                </span>
                <h3 className="mt-6 font-display text-2xl font-bold text-slate-50">Enquiry received.</h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-400">
                    Thank you, {form.name.split(' ')[0]}. A Planmodo practitioner will review your message
                    and respond within two business days.
                </p>
                <button
                    onClick={() => { setForm({ name: '', email: '', company: '', service: '', message: '' }); setStatus('idle'); }}
                    data-testid="contact-send-another-button"
                    className="mt-8 rounded-full border border-slate-500 px-6 py-3 text-sm font-semibold text-slate-200 transition-colors hover:border-deepteal/40 hover:text-deepteal"
                >
                    Send another enquiry
                </button>
            </motion.div>
        );
    }

    return (
        <form onSubmit={onSubmit} className="rounded-2xl border border-slate-800 bg-navy-800/40 p-7 sm:p-9" data-testid="contact-form">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                    <label htmlFor="contact-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Full name *</label>
                    <input id="contact-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={update('name')} placeholder="Jane Carter" className={inputClass} />
                </div>
                <div>
                    <label htmlFor="contact-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Work email *</label>
                    <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={update('email')} placeholder="jane@company.com" className={inputClass} />
                </div>
                <div>
                    <label htmlFor="contact-company" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Company</label>
                    <input id="contact-company" data-testid="contact-company-input" value={form.company} onChange={update('company')} placeholder="Company Inc." className={inputClass} />
                </div>
                <div>
                    <label htmlFor="contact-service" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Area of interest</label>
                    <select id="contact-service" data-testid="contact-service-select" value={form.service} onChange={update('service')} className={`${inputClass} appearance-none`}>
                        <option value="">General enquiry</option>
                        {SERVICES.map((s) => (
                            <option key={s.id} value={s.title}>{s.title}</option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="mt-5">
                <label htmlFor="contact-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">How can we help? *</label>
                <textarea
                    id="contact-message"
                    data-testid="contact-message-textarea"
                    required
                    minLength={10}
                    rows={5}
                    value={form.message}
                    onChange={update('message')}
                    placeholder="Tell us about your organization, your current resilience posture, and what prompted you to reach out."
                    className={`${inputClass} resize-none`}
                />
            </div>
            <button
                type="submit"
                disabled={status === 'sending'}
                data-testid="contact-form-submit-button"
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-glow px-8 py-4 text-sm font-semibold text-ink transition-all duration-200 hover:scale-[1.02] hover:shadow-xl hover:shadow-glow/25 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
                {status === 'sending' ? (
                    <><Loader2 size={16} className="animate-spin" /> Sending…</>
                ) : (
                    <>Request a Consultation <ArrowUpRight size={16} /></>
                )}
            </button>
            <p className="mt-4 text-xs text-slate-500">Your details are used only to respond to your enquiry.</p>
        </form>
    );
};

export default function Contact() {
    useSEO(
        'Contact Planmodo — Schedule a Resilience Consultation',
        'Schedule a consultation with a Planmodo practitioner. Response within two business days. Business continuity, disaster recovery and operational resilience.'
    );
    return (
        <>
            <section className="relative overflow-hidden pt-44 pb-20 sm:pb-28" data-testid="contact-hero">
                <div className="absolute inset-0 grid-lines opacity-60" />
                <div className="pointer-events-none absolute -top-24 right-1/4 h-96 w-96 rounded-full bg-deepteal/10 blur-[140px]" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
                        <Overline>Contact</Overline>
                    </motion.div>
                    <h1 className="mt-6 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-slate-50">
                        <span className="block overflow-hidden pb-1">
                            <motion.span className="block" initial={{ y: '110%' }} animate={{ y: '0%' }} transition={{ duration: 0.9, delay: 0.2, ease: EASE }}>
                                Start the <span className="text-deepteal">conversation.</span>
                            </motion.span>
                        </span>
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
                        className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300"
                    >
                        Whether you are building a resilience program from scratch or pressure-testing a mature one,
                        the first step is a conversation with a practitioner.
                    </motion.p>

                    <div className="mt-16 grid grid-cols-1 gap-12 lg:grid-cols-12">
                        <div className="lg:col-span-5">
                            <Reveal>
                                <div className="space-y-4">
                                    <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-card" className="group flex items-center gap-4 rounded-2xl border border-slate-800 bg-navy-800/40 p-5 transition-colors hover:border-deepteal/30">
                                        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-deepteal/10 border border-deepteal/20 text-deepteal"><Mail size={18} /></span>
                                        <div>
                                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Email</p>
                                            <p className="text-sm font-semibold text-slate-100 group-hover:text-deepteal">{CONTACT.email}</p>
                                        </div>
                                    </a>
                                    <div className="flex items-center gap-4 rounded-2xl border border-slate-800 bg-navy-800/40 p-5" data-testid="contact-response-card">
                                        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-deepteal/10 border border-deepteal/20 text-deepteal"><Globe size={18} /></span>
                                        <div>
                                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Coverage</p>
                                            <p className="text-sm font-semibold text-slate-100">Global · Remote-first, on-site when it matters</p>
                                        </div>
                                    </div>
                                </div>
                            </Reveal>

                            <div className="mt-12">
                                <h2 className="font-display text-lg font-semibold text-slate-50">What happens next</h2>
                                <div className="mt-6 space-y-6">
                                    {NEXT_STEPS.map((step, i) => (
                                        <Reveal key={step.title} delay={i * 0.08}>
                                            <div className="flex gap-4" data-testid={`contact-next-step-${i}`}>
                                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-deepteal/30 font-mono text-[11px] text-deepteal">{i + 1}</span>
                                                <div>
                                                    <p className="text-sm font-semibold text-slate-100">{step.title}</p>
                                                    <p className="mt-1 text-sm leading-relaxed text-slate-400">{step.text}</p>
                                                </div>
                                            </div>
                                        </Reveal>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-7">
                            <Reveal delay={0.1}>
                                <ContactForm />
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
